import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { InventoryEntities, InventoryModel } from 'client/data/models/inventory';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { RequestModel } from 'client/data/models/request';
import { get, flow, isNil } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { URL_CONTEXT_PATH, UrlModel } from 'client/data/models/url';
import { SEOModel } from 'client/data/models/seo';
import { connect } from 'react-redux';
import { featureFlags } from 'client/utils/feature-flags';
import { Usurp } from 'site-modules/inventory/pages/usurp/usurp';
import { checkCpoMessage, getCpoMakeName } from 'site-modules/shared/utils/cpo-message';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { withInventoryListTracking } from 'site-modules/shared/components/inventory-list-tracking/inventory-list-tracking';
import { withListTrackingData } from 'site-modules/shared/components/inventory-list-tracking/list-tracking-data';
import { InventoryDependencyContext } from 'site-modules/shared/components/inventory/inventory-dependency/inventory-dependency-context';
import { usurpDependencyContextValue } from 'site-modules/shared/components/inventory/inventory-dependency/usurp-dependency-context-value';
import { buildSearchIdPath, SemanticSearchModel } from 'client/data/models/semantic-search';

const SELECTED_FACETS_FALLBACK = {};

export const UsurpContainerUI = memo(
  ({
    visitorLocation = {},
    selectedFacets: selectedFacetsProp = {},
    urlContext = {},
    inventoryData: inventoryDataProp = null,
    isAkamaiCachePrefresh = false,
    isSearchBot = false,
    modifiedZip = undefined,
    cpoData = null,
    ...rest
  }) => {
    const [inventoryData, setInventoryData] = useState(inventoryDataProp);
    const selectedFacets = selectedFacetsProp || SELECTED_FACETS_FALLBACK;
    useEffect(() => {
      if (!isNil(inventoryDataProp)) {
        setInventoryData(inventoryDataProp);
      }
    }, [inventoryDataProp]);
    const isCrawler = Boolean(isAkamaiCachePrefresh || isSearchBot || featureFlags.get('enableBotRender'));

    return (
      <InventoryDependencyContext.Provider value={usurpDependencyContextValue}>
        <Usurp
          {...rest}
          inventoryData={inventoryData}
          selectedFacets={selectedFacets}
          visitorLocation={visitorLocation}
          urlContext={urlContext}
          isCrawler={isCrawler}
          modifiedZip={modifiedZip}
          cpoData={cpoData}
        />
      </InventoryDependencyContext.Provider>
    );
  }
);

UsurpContainerUI.displayName = 'UsurpContainerUI';

UsurpContainerUI.propTypes = {
  inventoryData: PropTypes.shape({
    inventories: PropTypes.shape({
      results: InventoryEntities.InventoriesVin,
      totalNumber: PropTypes.number,
      totalPages: PropTypes.number,
    }),
    facets: InventoryEntities.Facets,
    seoInfo: PropTypes.shape({}),
  }),
  isMobile: PropTypes.bool.isRequired,
  setModelValue: PropTypes.func.isRequired,
  selectedFacets: PropTypes.shape({}),
  urlContext: PropTypes.shape({}),
  zipCode: PropTypes.string.isRequired,
  location: PropTypes.shape({}),
  visitorLocation: VisitorEntities.Location,
  match: PropTypes.shape({
    params: PropTypes.shape({}),
  }),
  disableInventoryMLPhotoSort: PropTypes.bool,
  disableGeoLocation: PropTypes.bool,
  seoHeadContent: PropTypes.shape({}),
  isPrequalified: PropTypes.bool,
  isAkamaiCachePrefresh: PropTypes.bool,
  isSearchBot: PropTypes.bool,
  renderAsidePageContent: PropTypes.func,
  isVinPreview: PropTypes.bool,
  disableDrivewayNewCarLeadForms: PropTypes.bool,
  cpoData: PropTypes.shape({
    hasCpoMessage: PropTypes.bool,
    oemLogo: PropTypes.string,
  }),
  modifiedZip: PropTypes.string,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  disableInventoryMLPhotoSort: !!get(state, 'featureFlags["disable-inventory-ml-photo-sort"]'),
  disableGeoLocation: !!get(state, 'featureFlags["disable-geolocation"]'),
  isPrequalified: !!get(state, 'financing.prequalificationData'),
  disableDrivewayNewCarLeadForms: !!get(state, 'featureFlags["disableDrivewayNewCarLeadForms"]'),
});

export const stateToPropsConfig = {
  inventoryData: bindToPath('searchResults', InventoryModel, null, false),
  visitorLocation: bindToPath('location', VisitorModel, null, false),
  zipCode: bindToPath('location.zipCode', VisitorModel),
  seoHeadContent: bindToPath('headContent', SEOModel, null, false),
  isSearchBot: bindToPath('isSearchBot', RequestModel),
  isAkamaiCachePrefresh: bindToPath('isAkamaiCachePrefresh', RequestModel),
  modifiedZip: bindToPath(
    ({ searchId }) => (searchId ? `suggestedFilters["${searchId}"]` : null),
    SemanticSearchModel,
    semanticSearchSuggestedFilters => semanticSearchSuggestedFilters?.modifiedZip,
    false
  ),
  cpoData: bindToPath(
    ({ selectedFacets }) =>
      checkCpoMessage(selectedFacets) && `cpoData["${makeNiceName(getCpoMakeName(selectedFacets))}"]`,
    InventoryModel,
    null,
    false
  ),
};

export const UsurpContainer = flow(
  withInventoryListTracking,
  withListTrackingData,
  component => connectToModel(component, stateToPropsConfig),
  component =>
    connectToModel(component, {
      urlContext: bindToPath(URL_CONTEXT_PATH, UrlModel, null, false),
      selectedFacets: bindToPath('searchResultsFilter', InventoryModel, null, false),
      searchId: bindToPath(() => buildSearchIdPath(), SemanticSearchModel, null, false),
    }),
  connect(mapStateToProps)
)(UsurpContainerUI);
